import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList
} from "@wingspanhq/fe-component-library";
import { AccountTransaction } from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback, useMemo } from "react";
import { queriesInternalAccounts, queriesPersons } from "@query";
import { useSession } from "../../../../v3/hooks/useSession";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { disputeTransaction } from "../../utils/disputeTransaction";
import { buildInternalAccountTransactionsFiltersConfig } from "./filters/config";
import { createListRequest } from "./filters/request";
import { InternalAccountTransactionsFilter } from "./filters/types";
import { TableInternalAccountTransactions } from "./TableInternalAccountTransactions";

type Props = {
  internalAccountId?: string;
};

export const FilterableTableInternalAccountTransactions: React.FC<
  Props
> = props => {
  const { urlQuery, setUrlQuery } =
    useUrlQuery<InternalAccountTransactionsFilter>();

  const filtersConfig = useMemo(
    () => buildInternalAccountTransactionsFiltersConfig(),
    []
  );

  const hasAccount = !!props.internalAccountId;

  const request = useMemo(
    () => createListRequest(props.internalAccountId ?? "", urlQuery.filter),
    [props.internalAccountId, urlQuery.filter]
  );

  const query =
    queriesInternalAccounts.listInternalAccountTransactions.usePaginatedQuery(
      request,
      { enabled: hasAccount }
    );

  const items = query.flatData ?? [];

  const totalCount = query.data?.pages?.[0]?.pagination?.totalSize;

  const isLoading = hasAccount && query.isLoading;
  const hasFilters = !!request.filter;

  const session = useSession();
  const queryPerson = queriesPersons.getPerson.useQuery(
    { personId: session.personId },
    { enabled: !!session.personId }
  );
  const person = queryPerson.data;

  const onDispute = useCallback(
    (transaction: AccountTransaction) => {
      if (person) {
        disputeTransaction(transaction, person, session.personId);
      }
    },
    [person, session.personId]
  );

  const renderContent = () => {
    if (isLoading) {
      return <TableInternalAccountTransactions items={items} loading />;
    }

    if (query.isError) {
      return (
        <WSEmptyState
          type="error"
          title="Couldn't load transactions"
          description="Something went wrong loading transactions. Please try again."
          orientation="horizontal"
          actions={{
            buttons: [{ label: "Try again", onClick: () => query.refetch() }]
          }}
        />
      );
    }

    if (!hasAccount) {
      return (
        <WSEmptyState
          type="wallet"
          title="No account"
          description="There's no account to show transactions for."
        />
      );
    }

    if (items.length === 0) {
      return hasFilters ? (
        <WSEmptyState
          type="search"
          title="No matching transactions"
          description="No transactions match the selected filters."
        />
      ) : (
        <WSEmptyState
          type="documents"
          title="No transactions yet"
          description="Transactions will appear here once available."
        />
      );
    }

    return (
      <WSInfiniteScroll
        onLoad={() => query.fetchNextPage()}
        loadMore={items.length > 0}
        endOfList={!query.hasNextPage}
        loading={!!query.isFetchingNextPage}
      >
        <TableInternalAccountTransactions
          items={items}
          onDispute={person ? onDispute : undefined}
        />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="2XL">
      <WSFilters<InternalAccountTransactionsFilter>
        values={urlQuery.filter}
        onFilter={newFilter => {
          setUrlQuery(prev => ({ ...prev, filter: newFilter }));
        }}
        primaryFilters={filtersConfig}
        info={totalCount === undefined ? undefined : { count: totalCount }}
      />

      {renderContent()}
    </WSList>
  );
};
